'use client';

import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { DocsSidebar } from '@/components/docs/docs-sidebar';
import { DocsToc } from '@/components/docs/docs-toc';

export function DocsLayoutShell({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/82 backdrop-blur-2xl">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <a href="/docs" className="flex items-center gap-1.5">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/favicon.svg" alt="FluxPay" className="w-6 h-6" />
              <div>
                <span className="text-sm font-semibold tracking-wide uppercase hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
                <span className="text-sm font-semibold tracking-wide uppercase inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
                <span className="text-xs text-muted-foreground ml-1.5">Docs</span>
              </div>
            </a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/api-docs"
              className="hidden sm:inline-flex items-center px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-xl transition"
            >
              API Reference
            </a>
            <a
              href="/dashboard"
              className="hidden sm:inline-flex items-center px-4 py-2 bg-brand-gradient text-primary-foreground text-sm font-semibold rounded-xl shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.8)] hover:-translate-y-0.5 transition-all"
            >
              Dashboard
            </a>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar - Desktop */}
        <aside className="hidden lg:block w-[272px] flex-shrink-0 bg-card/45 border-r border-border/70 h-[calc(100vh-64px)] sticky top-16 overflow-y-auto backdrop-blur-xl">
          <DocsSidebar />
        </aside>

        {/* Left Sidebar - Mobile Overlay */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-30 bg-black/50 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed left-0 top-16 bottom-0 z-30 w-[280px] bg-card overflow-y-auto lg:hidden">
              <DocsSidebar />
            </aside>
          </>
        )}

        {/* Center Content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-[920px] mx-auto px-6 py-12 lg:px-12" id="docs-content">
            {children}
          </div>
        </main>

        {/* Right TOC Sidebar - Desktop only */}
        <aside className="hidden xl:block w-[200px] flex-shrink-0 py-10 pr-6">
          <DocsToc />
        </aside>
      </div>
    </div>
  );
}
